/**
 * Centralised session conflict guard.
 *
 * Any code that starts or navigates to a session should call `guardSession()`.
 * It checks for an active session conflict and, if one exists, shows an abandon
 * dialog. Once clear (no conflict, or user confirmed abandon), it calls `onProceed`.
 *
 * For resume paths (e.g. "Continue" on the home screen) use regular `navigate()`
 * — no guard needed because the user is returning to their own session.
 */
import { createContext, useContext, useCallback, useState, useRef, type ReactNode } from 'react';
import { useActiveSession, useAbandonSession } from '@/shared/api/hooks';
import { AbandonSessionDialog } from '@/shared/components/AbandonSessionDialog';

// ─── Public types ────────────────────────────────────────────────────────────

export interface GuardSessionOptions {
  /**
   * The lesson ID being navigated to. When provided and it matches the active
   * session's `sourceLessonId`, the guard treats this as a resume and calls
   * `onProceed` immediately (no dialog).
   *
   * Omit for revise sessions or any launch that should always conflict-check.
   */
  lessonId?: string;

  /** Called once the guard clears — either no conflict or user confirmed abandon. */
  onProceed: () => void;
}

interface SessionGuardContextValue {
  guardSession: (options: GuardSessionOptions) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const SessionGuardContext = createContext<SessionGuardContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function SessionGuardProvider({ children }: { children: ReactNode }) {
  const { data: activeSession } = useActiveSession();
  const abandonSession = useAbandonSession();

  const [showDialog, setShowDialog] = useState(false);
  const pendingProceedRef = useRef<(() => void) | null>(null);

  const guardSession = useCallback(
    ({ lessonId, onProceed }: GuardSessionOptions) => {
      // Don't queue a second dialog while one is already showing
      if (showDialog) return;

      // No active session → proceed immediately
      if (!activeSession) {
        onProceed();
        return;
      }

      // Same lesson → resume, proceed immediately
      if (lessonId && activeSession.sourceLessonId === lessonId) {
        onProceed();
        return;
      }

      // Conflict → show dialog, stash callback
      pendingProceedRef.current = onProceed;
      setShowDialog(true);
    },
    [activeSession, showDialog]
  );

  const handleAbandon = useCallback(() => {
    if (!activeSession) return;
    abandonSession.mutate(activeSession.id, {
      onSuccess: () => {
        setShowDialog(false);
        pendingProceedRef.current?.();
        pendingProceedRef.current = null;
      },
    });
  }, [activeSession, abandonSession]);

  const handleCancel = useCallback(() => {
    setShowDialog(false);
    pendingProceedRef.current = null;
  }, []);

  return (
    <SessionGuardContext.Provider value={{ guardSession }}>
      {children}
      <AbandonSessionDialog
        open={showDialog}
        onClose={handleCancel}
        onAbandon={handleAbandon}
        isPending={abandonSession.isPending}
        sessionType={activeSession?.studySessionType === 'revise' ? 'revise' : 'learn'}
        sessionTitle={activeSession?.title}
      />
    </SessionGuardContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSessionGuard() {
  const ctx = useContext(SessionGuardContext);
  if (!ctx) throw new Error('useSessionGuard must be used within SessionGuardProvider');
  return ctx;
}
